import { useFormik } from "formik";
import PropTypes from "prop-types";
import { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";

import { useChangeOwnPassword } from "../../../../hooks/useUser";

/**
 * Password change for the signed-in user.
 *
 * The only way to change a password used to be the forgot-password email OTP
 * flow, so an internal user had to lock themselves out to rotate their own
 * credentials.
 *
 * The current password is required: an authenticated session alone must not be
 * enough to take an account over.
 */
const ChangePasswordForm = ({ t }) => {
  const changePassword = useChangeOwnPassword();
  const [visible, setVisible] = useState({
    current: false,
    next: false,
    confirm: false,
  });

  const toggle = (field) =>
    setVisible((prev) => ({ ...prev, [field]: !prev[field] }));

  const validation = useFormik({
    initialValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      current_password: Yup.string().required(t("Le champ est requis")),
      new_password: Yup.string()
        .min(8, t("8 caractères minimum"))
        .matches(/[a-z]/, t("Doit contenir une minuscule"))
        .matches(/[A-Z]/, t("Doit contenir une majuscule"))
        .matches(/\d/, t("Doit contenir un chiffre"))
        .notOneOf(
          [Yup.ref("current_password")],
          t("Le nouveau mot de passe doit être différent de l'ancien")
        )
        .required(t("Le champ est requis")),
      confirm_password: Yup.string()
        .oneOf(
          [Yup.ref("new_password")],
          t("Les deux mots de passe ne correspondent pas")
        )
        .required(t("Le champ est requis")),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        await changePassword.mutateAsync(values);
        resetForm();
      } catch (_) {
        // handled by toast in hook
      }
    },
  });

  const renderField = (name, label, visibilityKey) => (
    <div className="mb-3">
      <Label className="form-label" htmlFor={name}>
        {label} <span style={{ color: "red" }}>*</span>
      </Label>
      <div className="position-relative auth-pass-inputgroup">
        <Input
          id={name}
          name={name}
          type={visible[visibilityKey] ? "text" : "password"}
          className="form-control pe-5"
          value={validation.values[name]}
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          autoComplete={
            name === "current_password" ? "current-password" : "new-password"
          }
          invalid={Boolean(
            validation.touched[name] && validation.errors[name]
          )}
        />
        <button
          type="button"
          className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
          onClick={() => toggle(visibilityKey)}
          tabIndex={-1}
        >
          <i
            className={
              visible[visibilityKey] ? "ri-eye-off-fill" : "ri-eye-fill"
            }
          ></i>
        </button>
        {validation.touched[name] && validation.errors[name] ? (
          <FormFeedback type="invalid">
            {validation.errors[name]}
          </FormFeedback>
        ) : null}
      </div>
    </div>
  );

  return (
    <Card>
      <CardBody>
        <div className="alert alert-info" role="alert">
          <i className="ri-information-line align-bottom me-1"></i>
          {t(
            "Après modification, vos autres sessions seront déconnectées par sécurité."
          )}
        </div>

        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}
        >
          <Row>
            <Col lg={12}>
              {renderField(
                "current_password",
                t("Mot de passe actuel"),
                "current"
              )}
            </Col>
            <Col lg={6}>
              {renderField("new_password", t("Nouveau mot de passe"), "next")}
            </Col>
            <Col lg={6}>
              {renderField(
                "confirm_password",
                t("Confirmer le nouveau mot de passe"),
                "confirm"
              )}
            </Col>
          </Row>

          <p className="text-muted fs-12">
            {t(
              "Le mot de passe doit contenir au moins 8 caractères, dont une majuscule, une minuscule et un chiffre."
            )}
          </p>

          <div className="text-end">
            <Button
              type="submit"
              color="warning"
              disabled={changePassword.isPending}
            >
              {changePassword.isPending ? (
                <Spinner size="sm" className="me-2" />
              ) : null}
              {t("Modifier le mot de passe")}
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  );
};

ChangePasswordForm.propTypes = {
  t: PropTypes.any,
};

export default ChangePasswordForm;
